import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { ChevronsUpDown, Check, Plus, Building2 } from "lucide-react";
import { toast } from "sonner";

export default function WorkspaceSwitcher({ user }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user?.role !== "client_view") api.get("/workspaces").then((r) => setItems(r.data)).catch(() => {}); }, [user?.workspace_id, user?.role]);

  const switchTo = async (ws) => {
    if (ws.current) return;
    try { const { data } = await api.post(`/workspaces/${ws.id}/switch`); localStorage.setItem("cf_token", data.token); toast.success(`Switched to ${data.workspace_name}`); window.location.href = "/deals"; }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const create = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/workspaces", { name }); localStorage.setItem("cf_token", data.token); toast.success(`Workspace "${data.name}" created`); window.location.href = "/deals"; }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); setBusy(false); }
  };
  const isClient = user?.role === "client_view";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild disabled={isClient}>
          <button className="w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-md border border-border bg-card shadow-sm text-left hover:border-primary/30 transition-colors duration-150" data-testid="sidebar-workspace">
            <div className="min-w-0"><div className="eyebrow">Workspace{items.length > 1 ? ` · ${items.length}` : ""}</div><div className="text-[13px] font-medium truncate">{user?.workspace_name}</div></div>
            {!isClient && <ChevronsUpDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 shadow-[var(--shadow-lift)]" data-testid="workspace-menu">
          <DropdownMenuLabel className="eyebrow">Your workspaces</DropdownMenuLabel>
          {items.map((w) => (
            <DropdownMenuItem key={w.id} onClick={() => switchTo(w)} data-testid={`workspace-${w.id}`} className="flex items-center gap-2">
              <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
              <div className="flex-1 min-w-0"><div className="text-[13px] truncate">{w.name}</div><div className="text-[10px] mono text-muted-foreground">{w.contacts} contacts · {w.deals_open} open deals</div></div>
              {w.current && <Check className="w-3.5 h-3.5 text-primary" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setOpen(true)} data-testid="workspace-new"><Plus className="w-3.5 h-3.5 mr-2" />New client workspace</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="workspace-dialog">
          <DialogHeader><DialogTitle>New client workspace</DialogTitle><DialogDescription>Agency mode: each client gets its own pipeline, contacts, sequences, inboxes and branding. Switch between them from the sidebar.</DialogDescription></DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <Input data-testid="workspace-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Bright Dental Group" required autoFocus />
            <Button type="submit" className="w-full" disabled={busy} data-testid="workspace-create">{busy ? "Creating..." : "Create & switch"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
